import { MerchantService } from "@/common/api.service";

// Language / Localization
import LanguageService from "@/common/language.service";

import {
	FETCH_MERCHANT,
	FETCH_BADGE,
	FETCH_RATES
} from "./actions.type";
import {
	RESOLVE_MERCHANT_INFO,
	RESOLVE_RATE_LIST,
	RESOLVE_BADGE_INFO,
	CALL_START,
	CALL_END
} from "./mutations.type";

const state = {
	fees: {
		"BANK": 0,
		"Default": 0.0225,
		"Card": 0.0225,
		"PAYPAL": 0.03,
		"OXXO": 0.03,
		"BOLETO": 0.03
	},
	staticFees: {
		"Default": 0.00
	},
	merchant: {},
	rates: [],
	badge: {},
	isLoading: false
};


const getters = {
	fees: state => state.fees,
	static_fees: state => state.staticFees,
	merchant: state => state.merchant,
	rates: state => state.rates,
	badge: state => state.badge,
	isLoading: state => state.isLoading
};

const actions = {
	[FETCH_MERCHANT](context, params) {
		// Start loading
		context.commit(CALL_START);

		// Grab our URL
		var host_client = location.host.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "").split('/')[0];

		// If local dev, swap for actual dev client
		if(location.host == "localhost:8080") {
			host_client = "hosted-invoice.rch.how";
		}

		// Ok, grab the Client's config data (maps to host url)
		return new Promise((resolve, reject) => {
			MerchantService.load_merchant_info({
					Client: host_client
				})
				.then(({ data }) => {
					// Double check we have an ID
					if(!data.MerchantId) {
						reject(LanguageService.getError('ClientNotFound'));
					}
					context.commit(RESOLVE_MERCHANT_INFO, data);
					context.commit(CALL_END);
					resolve();
				})
				.catch(error => {
					context.commit(CALL_END);
					reject(error);
				});
		});
	},
	[FETCH_RATES]({ commit }, params) {
		return new Promise((resolve, reject) => {
			MerchantService.load_merchant_rates({
				MerchantId: params.MerchantId
			})
			.then(({ data }) => {
				// Store data and resolve
				commit(RESOLVE_RATE_LIST, data);
				resolve();
			})
			.catch(error => {
				reject(LanguageService.getError('IncorrectMerchantConfig'));
			});
		});
	},
	[FETCH_BADGE]({ commit }, params) {
		return new Promise((resolve, reject) => {
			MerchantService.load_badge_info({
				MerchantId: params.MerchantId
			})
			.then(({ data }) => {
				// Store badge info and resolve
				commit(RESOLVE_BADGE_INFO, data);
				resolve();
			})
			.catch(error => {
				reject(LanguageService.getError('IncorrectMerchantConfig'));
			});
		});
	}
};

/* eslint no-param-reassign: ["error", { "props": false }] */
const mutations = {
	[CALL_START](state, data) {
		state.isLoading = true;
	},
	[RESOLVE_RATE_LIST](state, data ) {
		state.rates = data.RateOffers;
	},
	[RESOLVE_MERCHANT_INFO](state, merchant ) {
		state.merchant = merchant;
		// Use default system fees?
		if(!merchant.UseFeePresets) {
			state.merchant.fees = {};
		}
		// Load Merchant's static fees (ex $0.25 = 0.25)
		if(merchant.StaticFees !== undefined) {
			for (const [key, value] of Object.entries(merchant.StaticFees)) {
				state.staticFees[key] = value;
			}
		}
		// If the merchant config includes custom fees (Ex. 2% = 0.02), overwrite defaults
		if(merchant.Fees !== undefined) {
			for (const [key, value] of Object.entries(merchant.Fees)) {
				state.fees[key] = value;
			}
		}
		// Default to no external link.
		if(!merchant.ShowExternalInvoiceIdLink || !merchant.ExternalInvoiceIdLink) {
			state.merchant.ShowExternalInvoiceIdLink = false;
		} else {
			// If one of the children values is not set, turn off the link flag
			if(!merchant.ExternalInvoiceIdLink.Text || !merchant.ExternalInvoiceIdLink.LinkText || !merchant.ExternalInvoiceIdLink.TermsLink){
				state.merchant.ShowExternalInvoiceIdLink = false;
			}
		}
		// Default to no header link.
		if(!merchant.ShowHeaderInvoiceIdLink || !merchant.HeaderInvoiceIdLink) {
			state.merchant.ShowHeaderInvoiceIdLink = false;
		} else if(!merchant.HeaderInvoiceIdLink.Text || !merchant.HeaderInvoiceIdLink.LinkText || !merchant.HeaderInvoiceIdLink.TermsLink) {
			// If one of the children values is not set, turn off the link flag
			state.merchant.ShowHeaderInvoiceIdLink = false;
		}
		// Default to no footer link.
		if(!merchant.ShowFooterLink || !merchant.FooterLink) {
			state.merchant.ShowFooterLink = false;
		} else if(!merchant.FooterLink.Text || !merchant.FooterLink.LinkText || !merchant.FooterLink.TermsLink) {
			// If one of the children values is not set, turn off the link flag
			state.merchant.ShowFooterLink = false;
		}
		// Default to just Reach's T&C on review page
		if(!merchant.ShowMerchantTerms || !merchant.MerchantTerms) {
			state.merchant.ShowMerchantTerms = false;
			state.merchant.DisableReachTerms = false;
		} else {
			// If one of the children values is not set, turn off merchant terms
			if(!merchant.MerchantTerms.Text || !merchant.MerchantTerms.LinkText || !merchant.MerchantTerms.TermsLink){
				state.merchant.ShowMerchantTerms = false;
				state.merchant.DisableReachTerms = false;
			}
		}
		// Default a merchant to collect fees
		if(merchant.CollectFees === undefined) {
			state.merchant.CollectFees = true;
		}

		// Default to ViaAgent false
		if(merchant.ViaAgent === undefined || !merchant.ViaAgent) {
			state.merchant.ViaAgent = false;
		}

		// Default PresetRate
		if(merchant.ShowMerchantCurrency === undefined){
			state.merchant.ShowMerchantCurrency = true;
		}

		// Default PresetRate
		if(merchant.ShowPaymentSummary === undefined){
			state.merchant.ShowPaymentSummary = true;
		}
		// Default a merchant to allow any payments
		if(merchant.RequireParams === undefined) {
			state.merchant.RequireParams = false;
		}
		// Default a merchant to not require any url params
		if(merchant.RequiredUrlParams === undefined) {
			state.merchant.RequiredUrlParams = {
				"Country": false,
				"Invoice": false,
				"Amount": false
			};
		} else {
			// If undefined, null, not set, etc -> set to false
			if(!state.merchant.RequiredUrlParams.Country) state.merchant.RequiredUrlParams.Country = false;
			if(!state.merchant.RequiredUrlParams.Invoice) state.merchant.RequiredUrlParams.Invoice = false;
			if(!state.merchant.RequiredUrlParams.Amount) state.merchant.RequiredUrlParams.Amount = false;
		}

		// Default a merchant to show header
		if(merchant.HideHeader === undefined) {
			state.merchant.HideHeader = false;
		}
		// Default a merchant to show footer
		if(merchant.HideFooter === undefined) {
			state.merchant.HideFooter = true;
		}
		// Default merchant's theme
		if(merchant.Theme === undefined || !['theme-red', 'theme-green', 'theme-dhl', 'theme-jamiekay'].includes(merchant.Theme)) {
			state.merchant.Theme = 'theme-default';
		}
		// include merchant theme
		var file = document.createElement('link');
		file.rel = 'stylesheet';
		file.href = '/css/themes/'+state.merchant.Theme+'.css';
		document.head.appendChild(file);

		// Move HeaderText into text files (with backwards compatibility)
		if(state.merchant.Text === undefined) state.merchant.Text = {};
		if(merchant.HeaderText !== undefined) {
			state.merchant.Text.HeaderText = merchant.HeaderText;
		}
	},
	[RESOLVE_BADGE_INFO](state, data){
		state.badge = data;
	},
	[CALL_END](state, data) {
		state.isLoading = false;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};
